import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { relativeTime } from '../../lib/format';
import { useAsync, useDebounced } from '../../lib/hooks';
import { getRepo, type Article, type ArticleStatus } from '../../lib/repo';
import { STATUSES, STATUS_LABEL } from '../../shared/status.js';
import { Badge, Button, Card, EmptyState, Input, Spinner, Tabs, useToast } from '../../components/ui';

type Filter = 'all' | ArticleStatus;

const STATUS_OPTIONS: ArticleStatus[] = [...STATUSES];

export function ArticlesListPage() {
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const query = useDebounced(search, 250);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, loading, error, reload } = useAsync<Article[]>(
    () => getRepo().listAllArticles({ status: filter, query: query.trim() || undefined }),
    [filter, query],
  );

  const rows = data ?? [];

  async function remove(a: Article) {
    if (!window.confirm(`Delete “${a.title}”? This cannot be undone.`)) return;
    setBusyId(a.id);
    try {
      await getRepo().deleteArticle(a.id);
      toast('Article deleted');
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setBusyId(null);
    }
  }

  const tabs = [
    { value: 'all' as Filter, label: 'All' },
    ...STATUS_OPTIONS.map((s) => ({ value: s as Filter, label: STATUS_LABEL[s] ?? s })),
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Articles</h1>
          <p className="text-sm text-muted">{rows.length} shown{loading ? ' · loading…' : ''}</p>
        </div>
        <Link to="/admin/articles/new">
          <Button><Plus className="h-4 w-4" /> New article</Button>
        </Link>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search articles…"
          aria-label="Search articles"
          className="max-w-xs"
        />
        <Tabs tabs={tabs} value={filter} onChange={setFilter} />
      </div>

      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {loading ? <Spinner label="Loading articles…" /> : null}

      {!loading && rows.length === 0 ? (
        <EmptyState
          title="No articles here"
          description="Nothing matches this filter yet. Create an article to get started."
          action={<Link to="/admin/articles/new"><Button>New article</Button></Link>}
        />
      ) : null}

      {rows.length > 0 ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-background/60 text-left text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Updated</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((a) => {
                  const missingSeo = !a.seo.title || !a.seo.description;
                  const dateIso = a.publishedAt ?? a.scheduledFor ?? a.createdAt;
                  return (
                    <tr key={a.id} className="align-top hover:bg-background/40">
                      <td className="px-4 py-3">
                        <Link to={`/admin/articles/${a.id}`} className="font-medium hover:text-primary">{a.title}</Link>
                        <p className="mt-0.5 text-xs text-muted">
                          /article/{a.slug} · {a.author?.name ?? 'Unassigned'}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-muted">{a.category?.name ?? '—'}</td>
                      <td className="px-4 py-3"><Badge tone={a.status}>{STATUS_LABEL[a.status] ?? a.status}</Badge></td>
                      <td className="px-4 py-3 text-muted" title={dateIso}>{relativeTime(dateIso)}</td>
                      <td className="px-4 py-3 text-muted">{relativeTime(a.updatedAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {missingSeo ? (
                            <span
                              className="inline-flex items-center gap-1 text-[11px] text-warning"
                              title="Missing SEO title or meta description — this is what makes social unfurls wrong."
                            >
                              <AlertTriangle className="h-3.5 w-3.5" /> SEO
                            </span>
                          ) : (
                            <span className="text-[11px] text-success">SEO ok</span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Delete ${a.title}`}
                            disabled={busyId === a.id}
                            onClick={() => void remove(a)}
                          >
                            <Trash2 className="h-4 w-4 text-danger" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
